import { isRxError, RollbarRxError } from '@/lib/errors'
import { getAuthInstance } from '@/lib/firebase'
import { rollbarError, rollbarKey, rollbarTransform } from '@/lib/rollbar'
import Rollbar from 'rollbar/src/browser/core'
import instrumenter from 'rollbar/src/browser/telemetry'
import scrub from 'rollbar/src/scrub'
import telemeter from 'rollbar/src/telemetry'
import truncation from 'rollbar/src/truncation'
import type { App } from 'vue'

export function setupRollbar(codeVersion: string | undefined): Rollbar {
  Rollbar.setComponents({
    instrumenter,
    scrub,
    telemeter,
    truncation,
  })

  const rollbarConfig: Rollbar.Configuration = {
    transmit: import.meta.env.PROD,
    accessToken: 'f55fac490c924c19880f6ab680e3cdd1',
    captureUncaught: true,
    captureUnhandledRejections: true,
    autoInstrument: {
      network: true,
      log: true,
      dom: false,
      navigation: true,
      connectivity: true,
    },
    itemsPerMinute: 10,
    maxItems: 30,
    payload: {
      environment: import.meta.env.MODE,
      client: {
        javascript: {
          code_version: codeVersion,
          source_map_enabled: import.meta.env.PROD,
        },
      },
    },
    scrubFields: ['code', 'session_id'],
    scrubTelemetryInputs: true,
    scrubPaths: ['client.javascript.plugins'],
    transform: (payload) => {
      rollbarTransform(payload)
    },
    verbose: import.meta.env.DEV,
  }

  const rollbar = new Rollbar(rollbarConfig)

  if (typeof window !== 'undefined' && !window._rollbarStartTime) {
    window._rollbarStartTime = Date.now()
  }

  getAuthInstance().onAuthStateChanged((user) => {
    if (user) {
      rollbar.configure({
        payload: {
          person: { id: user.uid, email: user.email ?? undefined },
        },
      })
    } else {
      rollbar.configure({
        payload: { person: { id: null } },
      })
    }
  })

  return rollbar
}

export function RollbarPlugin(codeVersion: string | undefined) {
  return {
    install: (app: App) => {
      const rollbar = setupRollbar(codeVersion)
      app.config.errorHandler = (error, vm, info) => {
        if (isRxError(error)) {
          if (import.meta.env.DEV) {
            console.warn('errorHandler original RxError', error)
          }
          error = new RollbarRxError(error)
        }
        rollbar.error(rollbarError(error), { vueComponentName: vm?.$?.type?.name, info })
      }
      app.provide(rollbarKey, rollbar)
    },
  }
}
